import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output
} from '@angular/core';
import * as moment from 'moment';

@Component({
  selector: 't4-condition-editor',
  templateUrl: './t4-condition-editor.component.html',
  styleUrls: ['./t4-condition-editor.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class T4ConditionEditorComponent {

  @Input() conditions: T4ConditionEditorCondition[] = [];
  @Output() conditionsChange =
    new EventEmitter<T4ConditionEditorCondition[]>();

  @Input() fields: T4ConditionEditorFieldOption[] = [];
  @Input() operators: T4ConditionEditorOption[] = [];

  @Input() groups: T4ConditionEditorOption[] = [
    { Id: 1, Name: 'All' },
    { Id: 2, Name: 'Any' }
  ];

  @Input() defaultField?: number;
  @Input() defaultOperator?: number;

  public addCondition(groupId: number): void {
    const condition: T4ConditionEditorCondition = {
      ConditionGroup: groupId,
      Field: this.defaultField ?? this.fields[0]?.Id ?? 0,
      Operator: 1,
      Value: '',
      Value2: ''
    };

    this.conditions = [
      ...(this.conditions ?? []),
      condition
    ];

    this.emitChange();
  }

  public removeCondition(index: number): void {
    this.conditions = (this.conditions ?? []).filter(
      (_, conditionIndex) => conditionIndex !== index
    );

    this.emitChange();
  }

  public updateCondition(): void {
    this.conditions = [...(this.conditions ?? [])];
    this.emitChange();
  }

  public hasConditions(groupId: number): boolean {
    return (this.conditions ?? []).some(
      condition => condition.ConditionGroup === groupId
    );
  }

  public getField(
    fieldId: number
  ): T4ConditionEditorFieldOption | undefined {
    return this.fields.find(field => field.Id === fieldId);
  }

  public getFieldValueType(
    fieldId: number
  ): T4ConditionEditorValueType {
    return this.getField(fieldId)?.ValueType ?? 'text';
  }

  public getOperators(
    condition: T4ConditionEditorCondition
  ): T4ConditionEditorOption[] {

    const valueType = this.getFieldValueType(condition.Field);

    if (valueType === 'text') {
      return this.operators.filter(operator =>
        operator.Id === 1 ||
        operator.Id === 2 ||
        operator.Id === 3 ||
        operator.Id === 4 ||
        operator.Id === 5 ||
        operator.Id === 6);
    }

    if (valueType === 'number' || valueType === 'select') {
      return this.operators.filter(operator =>
        operator.Id === 1 ||
        operator.Id === 2);
    }

    if (valueType === 'date' || valueType === 'datetime') {
      return this.operators.filter(operator =>
        operator.Id === 1 ||
        operator.Id === 2 ||
        operator.Id === 7 ||
        operator.Id === 8 ||
        operator.Id === 9 ||
        operator.Id === 10 ||
        operator.Id === 11);
    }

    return this.operators;
  }

  public getFieldValues(
    condition: T4ConditionEditorCondition
  ): T4ConditionEditorOption[] {
    const field = this.getField(condition.Field);

    if (!field?.Values) {
      return [];
    }

    if (!field.Dependencies?.length) {
      return field.Values;
    }

    let availableValues = field.Values;

    for (const dependency of field.Dependencies) {
      const dependencyCondition =
        this.findDependencyCondition(
          condition.ConditionGroup,
          dependency.FieldId
        );

      if (!dependencyCondition?.Value) {
        continue;
      }

      const dependencyField =
        this.getField(dependency.FieldId);

      const selectedParentOption =
        dependencyField?.Values?.find(option =>
          this.getOptionValue(option) ===
          dependencyCondition.Value
        );

      if (!selectedParentOption) {
        continue;
      }

      if (dependency.FilterMode === 'parentId') {
        availableValues = availableValues.filter(
          option =>
            option.ParentId === selectedParentOption.Id
        );
      }

      if (dependency.FilterMode === 'parentIds') {
        availableValues = availableValues.filter(
          option =>
            option.ParentIds?.includes(
              selectedParentOption.Id
            ) === true
        );
      }

      if (dependency.FilterMode === 'conceptIds') {
        availableValues = availableValues.filter(
          option =>
            option.ConceptIds?.includes(
              selectedParentOption.Id
            ) === true
        );
      }
    }

    return availableValues;
  }

  public getOptionValue(
    option: T4ConditionEditorOption
  ): string {
    return option.Value ?? option.Id.toString();
  }

  public requiresSecondValue(operatorId: number): boolean {
    return operatorId === 6;
  }

  public onFieldChanged(condition: T4ConditionEditorCondition): void {
    condition.Operator = this.getOperators(condition)[0]?.Id ?? 1;
    condition.Value = '';
    condition.Value2 = '';

    this.clearDependentConditionValues(condition.ConditionGroup, condition.Field);
    this.updateCondition();
  }

  public onOperatorChanged(condition: T4ConditionEditorCondition): void {
    if (condition.Operator !== 1) {
      this.clearDependentConditionValues(
        condition.ConditionGroup,
        condition.Field
      );
    }

    if (!this.requiresSecondValue(condition.Operator)) {
      condition.Value2 = '';
    }

    this.updateCondition();
  }

  public onValueChanged(condition: T4ConditionEditorCondition): void {
    this.clearDependentConditionValues(condition.ConditionGroup, condition.Field);

    this.updateCondition();
  }

  private clearDependentConditionValues(conditionGroup: number, changedFieldId: number): void {
    const dependentFieldIds = this.fields
      .filter(field =>
        field.Dependencies?.some(dependency =>
            dependency.FieldId === changedFieldId
        )
      ).map(field => field.Id);

    if (dependentFieldIds.length === 0) {
      return;
    }

    for (const condition of this.conditions ?? []) {
      if (
        condition.ConditionGroup === conditionGroup &&
        dependentFieldIds.includes(condition.Field)
      ) {
        condition.Value = '';
        condition.Value2 = '';

        this.clearDependentConditionValues(conditionGroup, condition.Field);
      }
    }
  }

  private findDependencyCondition(conditionGroup: number, fieldId: number): T4ConditionEditorCondition | undefined {
    return (this.conditions ?? []).find(condition =>
      condition.ConditionGroup === conditionGroup &&
      condition.Field === fieldId &&
      condition.Operator === 1 &&
      !!condition.Value
    );
  }

  public getDateModel(
    condition: T4ConditionEditorCondition,
    property: 'Value' | 'Value2'
  ): moment.Moment | null {
    const value = condition[property];

    if (!value) {
      return null;
    }

    const parsedDate = moment(value, 'YYYY-MM-DD', true);

    return parsedDate.isValid()
      ? parsedDate
      : null;
  }

  public onDateChanged(
    condition: T4ConditionEditorCondition,
    property: 'Value' | 'Value2',
    selectedValue: any
  ): void {
    if (!selectedValue) {
      condition[property] = '';
      this.updateCondition();
      return;
    }

    const selectedDate = moment.isMoment(selectedValue) ? selectedValue : moment(selectedValue);

    condition[property] = selectedDate.isValid()
      ? selectedDate.format('YYYY-MM-DD')
      : '';

    this.updateCondition();
  }

  public onDateTimeChanged(
    condition: T4ConditionEditorCondition,
    property: 'Value' | 'Value2',
    value: string
  ): void {
    condition[property] = value || '';
    this.updateCondition();
  }

  public trackGroup(
    index: number,
    group: T4ConditionEditorOption
  ): number {
    return group.Id;
  }

  public trackCondition(
    index: number,
    condition: T4ConditionEditorCondition
  ): string | T4ConditionEditorCondition {
    return condition.Id ?? condition;
  }

  public trackOption(
    index: number,
    option: T4ConditionEditorOption
  ): number {
    return option.Id;
  }

  private emitChange(): void {
    this.conditionsChange.emit(this.conditions);
  }
}

export interface T4ConditionEditorCondition {
  Id?: string;
  ConditionGroup: number;
  Field: number;
  Operator: number;
  Value?: string;
  Value2?: string;
}

export interface T4ConditionEditorOption {
  Id: number;
  Name: string;
  Value?: string;
  ParentId?: number;
  ParentIds?: number[];
  ConceptIds?: number[];
}

export type T4ConditionEditorValueType =
  | 'text'
  | 'number'
  | 'date'
  | 'datetime'
  | 'select';

export type T4ConditionEditorDependencyFilterMode =
  | 'parentId'
  | 'parentIds';

export interface T4ConditionEditorFieldOption
  extends T4ConditionEditorOption {

  ValueType?:
  | 'text'
  | 'number'
  | 'date'
  | 'datetime'
  | 'select';

  Values?: T4ConditionEditorOption[];

  Dependencies?: T4ConditionEditorDependency[];
}
export interface T4ConditionEditorDependency {
  FieldId: number;
  FilterMode: 'parentId' | 'parentIds' | 'conceptIds';
}
